import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Layers, Save, Trash2, Upload } from 'lucide-react'
import {
  useCourse,
  useCourseModules,
  useCreateModule,
  useUpdateModule,
  useDeleteModule,
  useUploadCourseResources,
  useDeleteCourseResource,
} from '@/hooks/useCourses'
import { CourseModuleModel, CourseResourceModel } from '@/models/course.model'
import { paths } from '@/routes/path'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select } from '@/components/ui/Select'
import { FileUpload } from '@/components/ui/FileUpload'
import { Badge } from '@/components/ui/badge'

interface ModuleFormState {
  title: string
  description?: string
}

const emptyModuleForm: ModuleFormState = {
  title: '',
  description: '',
}

interface ResourceFormState {
  title: string
  description?: string
  type: 'document' | 'video'
  file: File | null
}

const emptyResourceForm: ResourceFormState = {
  title: '',
  description: '',
  type: 'document',
  file: null,
}

function formatDateLabel(value?: string) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function CourseModuleEditorPage() {
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId?: string }>()
  const isEditMode = Boolean(moduleId)
  const navigate = useNavigate()

  const [moduleForm, setModuleForm] = useState<ModuleFormState>({ ...emptyModuleForm })
  const [moduleError, setModuleError] = useState<string | null>(null)
  const [resourceError, setResourceError] = useState<string | null>(null)
  const [resourceSuccess, setResourceSuccess] = useState<string | null>(null)
  const [resourceForm, setResourceForm] = useState<ResourceFormState>({ ...emptyResourceForm })
  const [resourceBeingDeleted, setResourceBeingDeleted] = useState<string | null>(null)

  const { data: course, isLoading: isCourseLoading, error: courseError } = useCourse(courseId)
  const { data: modules = [], isLoading: isModulesLoading } = useCourseModules(courseId)
  const createModuleMutation = useCreateModule(courseId)
  const updateModuleMutation = useUpdateModule(courseId, moduleId)
  const deleteModuleMutation = useDeleteModule(courseId)
  const uploadResourceMutation = useUploadCourseResources(courseId)
  const deleteResourceMutation = useDeleteCourseResource(courseId)

  const activeModule: CourseModuleModel | undefined = useMemo(
    () => modules.find((module) => module.id === moduleId),
    [modules, moduleId],
  )

  const moduleResources: CourseResourceModel[] = useMemo(() => {
    if (!moduleId || !course?.resources) return []
    return course.resources
      .filter((resource) => resource.moduleId === moduleId)
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime()
        const dateB = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime()
        return dateB - dateA
      })
  }, [course, moduleId])

  const hasVideoResource = useMemo(
    () => moduleResources.some((resource) => (resource.type ?? '').toLowerCase().includes('video')),
    [moduleResources],
  )

  const nextModuleOrder = useMemo(() => {
    const modulesForOrdering = modules.length ? modules : course?.modules ?? []
    if (!modulesForOrdering.length) return 1

    const highestExplicitOrder = modulesForOrdering.reduce((maxOrder, module) => {
      const moduleOrder = typeof module.order === 'number' && Number.isFinite(module.order) ? module.order : null
      return moduleOrder !== null && moduleOrder > maxOrder ? moduleOrder : maxOrder
    }, 0)

    if (highestExplicitOrder > 0) {
      return highestExplicitOrder + 1
    }

    return modulesForOrdering.length + 1
  }, [modules, course])

  useEffect(() => {
    if (!isEditMode) {
      setModuleForm({ ...emptyModuleForm })
      return
    }
    if (activeModule) {
      setModuleForm({
        title: activeModule.title,
        description: activeModule.description ?? '',
      })
    }
  }, [isEditMode, activeModule])

  useEffect(() => {
    if (hasVideoResource && resourceForm.type === 'video') {
      setResourceForm((prev) => ({ ...prev, type: 'document' }))
    }
  }, [hasVideoResource, resourceForm.type])

  if (!courseId) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-secondary-500">
        Course identifier is required.
      </div>
    )
  }

  if (isCourseLoading || (isEditMode && isModulesLoading)) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-secondary-500">
        Loading module workspace…
      </div>
    )
  }

  if (courseError) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-secondary-500">
        <p className="text-lg font-semibold">Unable to load course details.</p>
        <Button variant="link" className="text-primary-600 hover:text-primary-700" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    )
  }

  if (!course) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-secondary-500">
        <p className="text-lg font-semibold">Course not found.</p>
        <Button variant="link" className="text-primary-600 hover:text-primary-700" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    )
  }

  if (isEditMode && !activeModule) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-secondary-500">
        <p className="text-lg font-semibold">Module not found.</p>
        <Button
          variant="link"
          className="text-primary-600 hover:text-primary-700"
          onClick={() => navigate(paths.app.courses.modules.root.build(course.id))}
        >
          Back to modules
        </Button>
      </div>
    )
  }

  const isSavingModule = createModuleMutation.isPending || updateModuleMutation.isPending
  const isUploadingResource = uploadResourceMutation.isPending
  const isDeletingResource = deleteResourceMutation.isPending
  const isDeletingModule = deleteModuleMutation.isPending

  const handleModuleChange = <T extends keyof ModuleFormState>(key: T, value: ModuleFormState[T]) => {
    setModuleForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleModuleSubmit = async () => {
    if (!courseId) return
    setModuleError(null)

    if (!moduleForm.title.trim()) {
      setModuleError('Module title is required.')
      return
    }

    const payload = {
      title: moduleForm.title.trim(),
      description: moduleForm.description?.trim() || undefined,
    }

    try {
      if (isEditMode && moduleId) {
        await updateModuleMutation.mutateAsync(payload)
      } else {
        await createModuleMutation.mutateAsync({
          ...payload,
          order: nextModuleOrder,
        })
      }
      navigate(paths.app.courses.modules.root.build(courseId), { replace: true })
    } catch (err) {
      setModuleError((err as Error).message || 'Unable to save module.')
    }
  }

  const handleModuleDelete = async () => {
    if (!courseId || !moduleId) return
    const confirmed = window.confirm('This will remove the module and its linked resource. Continue?')
    if (!confirmed) return

    try {
      await deleteModuleMutation.mutateAsync(moduleId)
      navigate(paths.app.courses.modules.root.build(courseId), { replace: true })
    } catch (err) {
      setModuleError((err as Error).message || 'Unable to delete module.')
    }
  }

  const handleResourceFieldChange = <T extends keyof ResourceFormState>(key: T, value: ResourceFormState[T]) => {
    setResourceForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleResourceSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!courseId || !moduleId) return
    setResourceError(null)
    setResourceSuccess(null)

    if (!resourceForm.title.trim()) {
      setResourceError('Resource title is required.')
      return
    }
    if (!resourceForm.file) {
      setResourceError('Attach a file to upload.')
      return
    }
    if (resourceForm.type === 'video' && hasVideoResource) {
      setResourceError('Only one video resource is allowed for each module.')
      return
    }

    try {
      await uploadResourceMutation.mutateAsync({
        title: resourceForm.title.trim(),
        description: resourceForm.description?.trim() || undefined,
        type: resourceForm.type,
        moduleId,
        files: [resourceForm.file],
      })
      setResourceSuccess('Resource uploaded successfully.')
      setResourceForm({ ...emptyResourceForm })
    } catch (err) {
      setResourceError((err as Error).message || 'Unable to upload resource.')
    }
  }

  const handleResourceRemove = async (resourceId: string) => {
    if (!courseId) return
    setResourceError(null)
    setResourceSuccess(null)
    const confirmed = window.confirm('Remove the attached resource from this module?')
    if (!confirmed) return

    try {
      setResourceBeingDeleted(resourceId)
      await deleteResourceMutation.mutateAsync(resourceId)
      setResourceSuccess('Resource removed from module.')
    } catch (err) {
      setResourceError((err as Error).message || 'Unable to remove resource.')
    } finally {
      setResourceBeingDeleted(null)
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm text-secondary-500">
            <Link
              to={paths.app.courses.modules.root.build(course.id)}
              className="inline-flex items-center gap-2 hover:text-secondary-700"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to course
            </Link>
            <span>/</span>
            <span className="inline-flex items-center gap-1">
              <Layers className="h-4 w-4" />
              {isEditMode ? 'Edit Module' : 'Create Module'}
            </span>
          </div>
          <h1 className="text-3xl font-bold text-secondary-900">{course.title}</h1>
          <p className="text-secondary-500">
            {isEditMode
              ? 'Update the selected module and manage its attached resources.'
              : 'Create a module to organise course content into structured sections.'}
          </p>
        </div>
        {isEditMode && (
          <Button
            variant="outline"
            className="border-secondary-200 text-secondary-600 hover:bg-secondary-50"
            onClick={() => navigate(paths.app.courses.content.build(course.id))}
          >
            Preview Learning Experience
          </Button>
        )}
      </div>

      {moduleError && (
        <div className="rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-600">
          {moduleError}
        </div>
      )}

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>{isEditMode ? 'Module details' : 'New module details'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">
              Module title <span className="text-primary-600">*</span>
            </label>
            <Input
              value={moduleForm.title}
              onChange={(event) => handleModuleChange('title', event.target.value)}
              placeholder="e.g., Social Media Strategies"
              disabled={isSavingModule}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">Description</label>
            <Textarea
              value={moduleForm.description ?? ''}
              onChange={(event) => handleModuleChange('description', event.target.value)}
              placeholder="Describe what learners will cover in this module."
              rows={4}
              disabled={isSavingModule}
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              className="bg-primary-600 text-white hover:bg-primary-700"
              onClick={handleModuleSubmit}
              disabled={isSavingModule}
            >
              <Save className="mr-2 h-4 w-4" />
              {isEditMode ? (isSavingModule ? 'Saving…' : 'Save Changes') : isSavingModule ? 'Creating…' : 'Create Module'}
            </Button>
            <Button
              variant="outline"
              className="border-secondary-200 text-secondary-600 hover:bg-secondary-50"
              onClick={() => navigate(paths.app.courses.modules.root.build(course.id))}
              disabled={isSavingModule}
            >
              Cancel
            </Button>
            {isEditMode && (
              <Button
                variant="ghost"
                className="text-red-600 hover:bg-red-50"
                onClick={handleModuleDelete}
                disabled={isDeletingModule}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {isDeletingModule ? 'Deleting…' : 'Delete module'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-6">
        {isEditMode ? (
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle>Module resources</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {resourceError && (
                <div className="rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-600">
                  {resourceError}
                </div>
              )}
              {resourceSuccess && (
                <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                  {resourceSuccess}
                </div>
              )}

              <div className="space-y-4">
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                  <form className="space-y-4" onSubmit={handleResourceSubmit}>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-secondary-700">
                        Resource title <span className="text-primary-600">*</span>
                      </label>
                      <Input
                        value={resourceForm.title}
                        onChange={(event) => handleResourceFieldChange('title', event.target.value)}
                        placeholder="e.g., Social Media Strategy Summary"
                        disabled={isUploadingResource}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-secondary-700">Resource type</label>
                      <Select
                        value={resourceForm.type}
                        onChange={(event) =>
                          handleResourceFieldChange('type', event.target.value as ResourceFormState['type'])
                        }
                        disabled={isUploadingResource}
                      >
                        <option value="document">Document</option>
                        <option value="video" disabled={hasVideoResource}>
                          {hasVideoResource ? 'Video (already uploaded)' : 'Video'}
                        </option>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-secondary-700">Description</label>
                      <Textarea
                        value={resourceForm.description ?? ''}
                        onChange={(event) => handleResourceFieldChange('description', event.target.value)}
                        placeholder="Provide additional context for learners."
                        rows={3}
                        disabled={isUploadingResource}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-secondary-700">
                        Upload file <span className="text-primary-600">*</span>
                      </label>
                      <FileUpload
                        onFileSelect={(files) => {
                          const firstFile = files?.item(0) ?? null
                          handleResourceFieldChange('file', firstFile)
                        }}
                        helperText="Supported formats: PDF, DOCX, MP4. Max size 500MB. Multiple documents allowed; limit one video per module."
                      />
                      {resourceForm.file && (
                        <p className="text-sm text-secondary-600">
                          Selected file: <span className="font-medium text-secondary-900">{resourceForm.file.name}</span>
                        </p>
                      )}
                    </div>

                    <div className="flex justify-end gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        className="border-secondary-200 text-secondary-600 hover:bg-secondary-50"
                        onClick={() => setResourceForm({ ...emptyResourceForm })}
                        disabled={isUploadingResource}
                      >
                        Clear
                      </Button>
                      <Button
                        type="submit"
                        className="bg-primary-600 text-white hover:bg-primary-700"
                        disabled={isUploadingResource}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {isUploadingResource ? 'Uploading…' : 'Upload resource'}
                      </Button>
                    </div>
                  </form>

                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-secondary-700">Attached resources</h3>
                    {moduleResources.length > 0 ? (
                      <div className="space-y-4">
                        {moduleResources.map((resource) => {
                          const resourceType = (resource.type ?? '').toLowerCase()
                          const isVideoResource = resourceType.includes('video')
                          return (
                            <div
                              key={resource.id}
                              className="rounded-md border border-secondary-200 bg-secondary-50 p-4"
                            >
                              <div className="flex flex-col gap-4 md:flex-row md:justify-between">
                                <div className="space-y-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    {resource.processingStatus && (
                                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                                        {resource.processingStatus}
                                      </Badge>
                                    )}
                                    <Badge variant="outline" className="border-primary-200 text-primary-700">
                                      {(resource.type ?? 'document').toUpperCase()}
                                    </Badge>
                                  </div>
                                  <div>
                                    <h3 className="text-lg font-semibold text-secondary-900">{resource.title}</h3>
                                    {resource.description && (
                                      <p className="mt-1 text-sm text-secondary-600">{resource.description}</p>
                                    )}
                                  </div>
                                  <p className="text-xs text-secondary-500">
                                    Uploaded {formatDateLabel(resource.updatedAt ?? resource.createdAt)}
                                  </p>
                                </div>
                                <div className="flex flex-col items-end justify-between gap-3 text-right text-sm text-secondary-500">
                                  <div>
                                    {resource.fileSize
                                      ? `${(resource.fileSize / (1024 * 1024)).toFixed(1)} MB`
                                      : 'Size unknown'}
                                  </div>
                                  <Button
                                    variant="outline"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => handleResourceRemove(resource.id)}
                                    disabled={isDeletingResource}
                                  >
                                    {isDeletingResource && resourceBeingDeleted === resource.id
                                      ? 'Removing…'
                                      : `Remove ${isVideoResource ? 'video' : 'resource'}`}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="rounded-md border border-dashed border-secondary-200 bg-white px-4 py-3 text-sm text-secondary-500">
                        No resources attached yet. Upload documents or a single video to enrich this module.
                      </p>
                    )}
                  </div>

                </div>
                

                
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-dashed border-primary-200 bg-primary-50/40">
            <CardContent className="space-y-3 py-6 text-secondary-600">
              <h3 className="text-lg font-semibold text-secondary-900">What happens next?</h3>
              <p className="text-sm">
                Once the module is created, you will be able to attach supporting resources such as documents and a
                single video. Multiple documents are supported, but only one video can be linked to each module.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
